/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 **************************************************************************/
export var ShowModalResultType;
(function (ShowModalResultType) {
    // The dialog was not shown because the host has not provided support for it.
    ShowModalResultType["NotImplemented"] = "not_implemented";
    // The user followed the dialog flow to completion.
    ShowModalResultType["Success"] = "success";
    // The user canceled the dialog.
    ShowModalResultType["Cancel"] = "cancel";
    // The user interacted with the host in a way that closed the dialog (e.g. pressing escape,
    // clicking outside the dialog, or clicking the close button at the top right).
    ShowModalResultType["Close"] = "close";
    // The dialog was closed as part of an error flow, typically accompanied by a toast.
    ShowModalResultType["Error"] = "error";
})(ShowModalResultType || (ShowModalResultType = {}));
// TODO: https://jira.corp.adobe.com/browse/CCXCD-14030
// Import these types and enums below from component-types into CDO/UAB
/* eslint-disable @typescript-eslint/no-namespace */
export var CDO;
(function (CDO) {
    let ActionType;
    (function (ActionType) {
        /** Default action type. This action is shown for each asset in the Action menu, Context Menu and Action Bar */
        ActionType["Asset"] = "asset";
        /** This action applies to the currently navigated route in CDO. It will be rendered in the Header of the provided route/tab
         * Example: New Folder, Upload, etc.
         */
        ActionType["Route"] = "route";
    })(ActionType = CDO.ActionType || (CDO.ActionType = {}));
    let ClickSelectType;
    (function (ClickSelectType) {
        ClickSelectType["Single"] = "single";
        ClickSelectType["Double"] = "double";
        ClickSelectType["None"] = "none";
    })(ClickSelectType = CDO.ClickSelectType || (CDO.ClickSelectType = {}));
})(CDO || (CDO = {}));
export var ContentViewType;
(function (ContentViewType) {
    ContentViewType["CloudDocuments"] = "cloud-documents";
    ContentViewType["Deleted"] = "deleted";
    ContentViewType["SharedWithYou"] = "shared-with-you";
    ContentViewType["Recents"] = "recents";
    ContentViewType["ReviewLinks"] = "review-links";
    ContentViewType["ConvertToSketch"] = "sketch";
    ContentViewType["Libraries"] = "libraries";
    ContentViewType["Local"] = "local";
    ContentViewType["Projects"] = "projects";
    ContentViewType["Project"] = "project";
    ContentViewType["GenerationHistory"] = "generation-history";
    ContentViewType["FFBoards"] = "ffboards";
    ContentViewType["Favorites"] = "favorites";
    ContentViewType["AssetPreview"] = "asset-preview";
})(ContentViewType || (ContentViewType = {}));
export var OtherFileTypeValues;
(function (OtherFileTypeValues) {
    OtherFileTypeValues["AllFiles"] = "ALL_FILES";
    OtherFileTypeValues["Folder"] = "FOLDER";
    OtherFileTypeValues["Project"] = "PROJECT";
    OtherFileTypeValues["ReviewLink"] = "REVIEW_LINK";
})(OtherFileTypeValues || (OtherFileTypeValues = {}));
export var GenericFileTypeValues;
(function (GenericFileTypeValues) {
    GenericFileTypeValues["Assets3d"] = "ASSETS_3D";
    GenericFileTypeValues["Images"] = "IMAGES";
    GenericFileTypeValues["Videos"] = "VIDEOS";
    GenericFileTypeValues["Audio"] = "AUDIO";
})(GenericFileTypeValues || (GenericFileTypeValues = {}));
export var AdobeFileTypeValues;
(function (AdobeFileTypeValues) {
    AdobeFileTypeValues["Photoshop"] = "PHOTOSHOP";
    AdobeFileTypeValues["Illustrator"] = "ILLUSTRATOR";
    AdobeFileTypeValues["Express"] = "EXPRESS";
    AdobeFileTypeValues["Acrobat"] = "PDF";
    AdobeFileTypeValues["InDesign"] = "INDESIGN";
    AdobeFileTypeValues["Aero"] = "AERO";
    AdobeFileTypeValues["XD"] = "XD";
    AdobeFileTypeValues["Concept"] = "CONCEPT";
    AdobeFileTypeValues["Squirrel"] = "SQUIRREL";
})(AdobeFileTypeValues || (AdobeFileTypeValues = {}));
export var LibrariesAndBrandsTypeValues;
(function (LibrariesAndBrandsTypeValues) {
    LibrariesAndBrandsTypeValues["Libraries"] = "LIBRARIES";
    LibrariesAndBrandsTypeValues["Brands"] = "BRANDS";
})(LibrariesAndBrandsTypeValues || (LibrariesAndBrandsTypeValues = {}));
export var FireflyGenerationTypeValues;
(function (FireflyGenerationTypeValues) {
    FireflyGenerationTypeValues["GenerationAll"] = "FIREFLY_GENERATION_ALL";
    FireflyGenerationTypeValues["GenerationImage"] = "FIREFLY_GENERATION_IMAGE";
    FireflyGenerationTypeValues["GenerationVideo"] = "FIREFLY_GENERATION_VIDEO";
})(FireflyGenerationTypeValues || (FireflyGenerationTypeValues = {}));
export var SharedViewTypeValues;
(function (SharedViewTypeValues) {
    SharedViewTypeValues["All"] = "SHARED_VIEW_ALL";
    SharedViewTypeValues["Yours"] = "SHARED_VIEW_YOURS";
    SharedViewTypeValues["SharedWithYou"] = "SHARED_VIEW_SHARED_WITH_YOU";
})(SharedViewTypeValues || (SharedViewTypeValues = {}));
export const FileFilterTypeValues = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, GenericFileTypeValues), AdobeFileTypeValues), OtherFileTypeValues), LibrariesAndBrandsTypeValues), FireflyGenerationTypeValues), SharedViewTypeValues);
export var SAMLogLevel;
(function (SAMLogLevel) {
    SAMLogLevel[SAMLogLevel["Off"] = 0] = "Off";
    SAMLogLevel[SAMLogLevel["Medium"] = 1] = "Medium";
    SAMLogLevel[SAMLogLevel["High"] = 2] = "High";
})(SAMLogLevel || (SAMLogLevel = {}));
export var CDOLogLevel;
(function (CDOLogLevel) {
    CDOLogLevel[CDOLogLevel["DEBUG"] = 0] = "DEBUG";
    CDOLogLevel[CDOLogLevel["INFO"] = 1] = "INFO";
    CDOLogLevel[CDOLogLevel["WARN"] = 2] = "WARN";
    CDOLogLevel[CDOLogLevel["ERROR"] = 3] = "ERROR";
    CDOLogLevel[CDOLogLevel["DISABLED"] = 4] = "DISABLED";
})(CDOLogLevel || (CDOLogLevel = {}));
export var AccountType;
(function (AccountType) {
    AccountType["Personal"] = "type1";
    AccountType["Enterprise"] = "type2";
    AccountType["Business"] = "type3";
    AccountType["Federated"] = "type2e";
})(AccountType || (AccountType = {}));
/**
 * PickerMode is an enum for quickly configuring parts of CDP UI to show/hide various
 * features including tabs, buttons, and behvaioral changes. Modes can be sent through several APIs including through
 * Panel.tsx, Commands.tsx, and WebCommands.tsx, returned from host.getPickerInfo() calls and
 * through a PICKERSTATECHANGED event sent from UXP which provides the new mode.
 * The mode is also sent through executeValidPickerAction call into the host.
 */
export var PickerMode;
(function (PickerMode) {
    /**
     * Place mode that is for the 2021 Place workflow. This mode will be just like Open but will have different
     * analytics and a different callback.
     */
    PickerMode["placeLinkOrEmbed"] = "placeLinkOrEmbed";
    /**
     * Link Assets mode that is primarily used with XD. This mode is not considered a "Save As" mode meaning
     * it's primarily used for "Opening Documents" scenarios.
     * Visible Tabs - Cloud Documents, Shared With You, Convert To Sketch Libraries.
     */
    PickerMode["linkAssets"] = "linkAssets";
    /**
     * The primary mode for users when selecting documents to open. When a document is clicked, it's
     * selected and then highlights the "Open" button so the user can opened the selected document.
     * This mode is not considered a "Save As" view mode.
     * Visible Tabs - Recent Tab, Cloud Documents Tab, Shared With you tab.
     */
    PickerMode["open"] = "open";
    /**
     * The primary mode for CDP when Saving Documents to the host application. Shows the "Save As" header
     * with an input for the user to type. This mode is unlike "Opening" modes in that the users is supposed
     * to provide a name for the file to be saved. It  shows a "Save" Button and no "On Your Computer"
     * buttons. When an item is clicked, we set the Save As Input field to the name of the item that was clicked.
     */
    PickerMode["saveAs"] = "saveAs";
    /**
     * This is another "Save As" mode that shoud be identical to PickerMode.saveAs. The main difference
     * here is for the host app to know when a user invoked Save As Copy instead of just a plain
     * Save As. When an item is clicked, we set the Save As Input field to the name of the item that was clicked.
     */
    PickerMode["saveAsCopy"] = "saveAsCopy";
    PickerMode["saveToFolder"] = "saveToFolder";
    /**
     * This is another "Save As" mode that shoud be similar (not identical) to PickerMode.saveAs. Header and
     * primaryButton labels will display 'export' specific labelling. Like "Save As", user can update the name of
     * exported asset and choose the export format.
     */
    PickerMode["export"] = "export";
    /**
     * Copy items to Projects or Cloud Documents.
     */
    PickerMode["copy"] = "copy";
    /**
     * Generic move for moving items to new location.
     */
    PickerMode["move"] = "move";
    /**
     * Move item(s) to a target location. (e.g. assets from Files to Projects)
     */
    PickerMode["moveFrom"] = "moveFrom";
    /**
     * Generic mode for selecting an asset and returning it to the caller
     */
    PickerMode["add"] = "add";
})(PickerMode || (PickerMode = {}));
export var GetBlockedAssetsResponse;
(function (GetBlockedAssetsResponse) {
    /**
     * Proceed with the operation
     */
    GetBlockedAssetsResponse["Ok"] = "ok";
    /**
     * Skip the operation for this asset, perhaps because the host application
     * performs the operation instead, or the operation is not supported or
     * some other reason
     */
    GetBlockedAssetsResponse["Cancel"] = "cancel";
    /**
     * Skip the operation for this asset, and notify the user that it could not
     * be performed because the asset is open in the host application
     */
    GetBlockedAssetsResponse["CancelBecauseOpen"] = "cancel-open";
})(GetBlockedAssetsResponse || (GetBlockedAssetsResponse = {}));
export var Capability;
(function (Capability) {
    Capability["ShareSheetInvite"] = "invite";
    Capability["ShareSheetLink"] = "getLink";
    Capability["ShareSheetAction"] = "share";
})(Capability || (Capability = {}));
